import { FC, FormEvent, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useNotification } from 'lib/context/notification.context'
import {
  createInventoryInvoice,
  deleteInventoryInvoice,
  getInventoryInvoices,
  InventoryInvoice,
  updateInventoryInvoice,
} from 'services/inventory/invoices'
import { Button } from 'views/components/UI/Buttons'
import { EditInventoryForm, EditFormRow, EditFormLabel, InventoryDetailHeader } from '../super-admin/inventory/styled'

interface InventoryInvoicesDialogProps {
  inventory: { id: number; name: string; companyName?: string }
  onClose?: () => void
  embedded?: boolean
}

dayjs.extend(utc)

const InvoiceForm = styled(EditInventoryForm)<{ $embedded: boolean }>`
  && {
    grid-template-columns: ${({ $embedded }) => $embedded
      ? 'repeat(3, minmax(0, 1fr)) 350px'
      : 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))'};
  }

  && > ${EditFormRow} {
    grid-column: auto;
  }

  && > .invoice-form-action {
    grid-column: ${({ $embedded }) => $embedded ? 'auto' : '1 / -1'};
  }

  @media (max-width: 1340px) {
    && {
      grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr));
    }

    && > .invoice-form-action {
      grid-column: 1 / -1;
    }
  }

  .MuiOutlinedInput-root {
    background: #f7fafc;
  }

  .MuiOutlinedInput-root:not(.Mui-focused) .MuiOutlinedInput-notchedOutline {
    border-color: #cbd8e3;
  }

  .invoice-form-action {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 10px;
  }
`

const InvoiceFormButton = styled(Button)`
  && {
    margin: 0;
    min-height: 40px;
    border-radius: 12px;
    background: ${({ variant }) => variant === 'secondary' ? '#eaf3f8' : '#397a9d'};
    border: 1px solid ${({ variant }) => variant === 'secondary' ? '#b9d2e0' : '#397a9d'};
    color: ${({ variant }) => variant === 'secondary' ? '#286384' : '#ffffff'};
    box-shadow: 0 3px 9px rgba(37, 92, 123, 0.12);
  }

  &&:hover {
    transform: none;
    filter: none;
    background: ${({ variant }) => variant === 'secondary' ? '#dcecf4' : '#2e698a'};
    border-color: ${({ variant }) => variant === 'secondary' ? '#9fc4d7' : '#2e698a'};
    box-shadow: 0 5px 12px rgba(37, 92, 123, 0.18);
  }

  &&:active {
    transform: none;
    box-shadow: 0 2px 5px rgba(37, 92, 123, 0.14);
  }

  &&:disabled {
    background: #eef2f6;
    border-color: #d7e0ea;
    color: #a3afbb;
    box-shadow: none;
  }
`

const isValidDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00.000Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
}

const InventoryInvoicesDialog: FC<InventoryInvoicesDialogProps> = ({ inventory, onClose, embedded = false }) => {
  const { getSuccess } = useNotification()
  const formRef = useRef<HTMLFormElement>(null)
  const [invoices, setInvoices] = useState<InventoryInvoice[]>([])
  const [invoiceNumber, setInvoiceNumber] = useState<string>('')
  const [invoiceDate, setInvoiceDate] = useState<string>('')
  const [amount, setAmount] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null)
  const [invoiceToDelete, setInvoiceToDelete] = useState<InventoryInvoice | null>(null)
  const [listError, setListError] = useState<string>('')
  const [formError, setFormError] = useState<string>('')
  const [deleteError, setDeleteError] = useState<string>('')
  const [refresh, setRefresh] = useState<number>(0)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    getInventoryInvoices(inventory.id)
      .then((data: InventoryInvoice[]) => {
        if (!active) return
        setInvoices(data)
        setListError('')
      })
      .catch(() => {
        if (!active) return
        setInvoices([])
        setListError('No se pudieron cargar las facturas.')
      })
      .finally(() => { if (active) setIsLoading(false) })
    return () => { active = false }
  }, [inventory.id, refresh])

  const resetForm = (): void => {
    setEditingInvoiceId(null)
    setInvoiceNumber('')
    setInvoiceDate('')
    setAmount('')
    setFormError('')
  }

  const startEditing = (invoice: InventoryInvoice): void => {
    setEditingInvoiceId(invoice.id)
    setInvoiceNumber(invoice.invoice_number)
    setInvoiceDate(dayjs.utc(invoice.invoice_date).format('YYYY-MM-DD'))
    setAmount(invoice.amount)
    setFormError('')
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const saveInvoice = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (isSaving) return

    const normalizedNumber = invoiceNumber.trim()
    const normalizedAmount = amount.trim().replace(',', '.')
    if (!normalizedNumber || normalizedNumber.length > 100) {
      setFormError('Indica un número de factura de hasta 100 caracteres.')
      return
    }
    if (!isValidDate(invoiceDate)) {
      setFormError('Indica una fecha de factura válida.')
      return
    }
    if (!/^(?:0|[1-9]\d{0,12})(?:\.\d{1,2})?$/.test(normalizedAmount) || Number(normalizedAmount) <= 0) {
      setFormError('Indica un importe positivo con hasta dos decimales.')
      return
    }

    setIsSaving(true)
    setFormError('')
    try {
      const invoice = {
        invoice_number: normalizedNumber,
        invoice_date: invoiceDate,
        amount: normalizedAmount,
      }
      if (editingInvoiceId === null) {
        await createInventoryInvoice(inventory.id, invoice)
      } else {
        await updateInventoryInvoice(inventory.id, editingInvoiceId, invoice)
      }
      getSuccess(editingInvoiceId === null ? 'Factura registrada' : 'Factura actualizada')
      resetForm()
      setRefresh((value: number) => value + 1)
    } catch (error) {
      setFormError(editingInvoiceId === null ? 'No se pudo registrar la factura.' : 'No se pudo actualizar la factura.')
    } finally {
      setIsSaving(false)
    }
  }

  const confirmDelete = async (): Promise<void> => {
    if (!invoiceToDelete || isDeleting) return
    setIsDeleting(true)
    setDeleteError('')
    try {
      await deleteInventoryInvoice(inventory.id, invoiceToDelete.id)
      if (editingInvoiceId === invoiceToDelete.id) resetForm()
      setInvoiceToDelete(null)
      setRefresh((value: number) => value + 1)
      getSuccess('Factura eliminada')
    } catch (error) {
      setDeleteError('No se pudo eliminar la factura.')
    } finally {
      setIsDeleting(false)
    }
  }

  const content = (
    <Box component="form" ref={formRef} onSubmit={saveInvoice}>
      <DialogContent>
        {inventory.companyName && <Typography color="text.secondary" sx={{ mb: 2 }}>{inventory.companyName}</Typography>}
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {editingInvoiceId === null ? 'Registrar factura' : 'Editar factura'}
        </Typography>
        <InvoiceForm $embedded={embedded}>
          <EditFormRow>
            <EditFormLabel htmlFor="inventory-invoice-number">Número de factura</EditFormLabel>
            <TextField id="inventory-invoice-number" value={invoiceNumber} onChange={(event) => setInvoiceNumber(event.target.value)} inputProps={{ maxLength: 100 }} required fullWidth disabled={isSaving} size="small" variant="outlined" />
          </EditFormRow>
          <EditFormRow>
            <EditFormLabel htmlFor="inventory-invoice-date">Fecha de factura</EditFormLabel>
            <TextField id="inventory-invoice-date" type="date" value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} required fullWidth disabled={isSaving} size="small" variant="outlined" />
          </EditFormRow>
          <EditFormRow>
            <EditFormLabel htmlFor="inventory-invoice-amount">Importe</EditFormLabel>
            <TextField id="inventory-invoice-amount" value={amount} onChange={(event) => setAmount(event.target.value)} inputProps={{ inputMode: 'decimal' }} required fullWidth disabled={isSaving} size="small" variant="outlined" />
          </EditFormRow>
          <EditFormRow className="invoice-form-action">
            {editingInvoiceId !== null && <InvoiceFormButton type="button" variant="secondary" onClick={resetForm} disabled={isSaving}>Cancelar edición</InvoiceFormButton>}
            <InvoiceFormButton type="submit" disabled={isSaving}>
              {editingInvoiceId === null ? 'Registrar factura' : 'Guardar cambios'}
            </InvoiceFormButton>
          </EditFormRow>
        </InvoiceForm>
        {formError && <Typography color="error" sx={{ mt: 1 }}>{formError}</Typography>}
        <Box sx={{ borderTop: '1px solid #e5edf4', mt: 3, pt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Facturas registradas</Typography>
          {isLoading && <CircularProgress size={24} aria-label="Cargando facturas" />}
          {listError && <Typography color="error" sx={{ mb: 1 }}>{listError}</Typography>}
          {!isLoading && !listError && invoices.length === 0 && <Typography>No hay facturas registradas.</Typography>}
          {!isLoading && !listError && invoices.length > 0 && <TableContainer sx={{ maxHeight: 320 }}>
            <Table stickyHeader size="small" aria-label="Facturas del inventario">
              <TableHead>
                <TableRow>
                  <TableCell>Número</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell align="right">Importe</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{invoices.map((invoice: InventoryInvoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.invoice_number}</TableCell>
                  <TableCell>{dayjs.utc(invoice.invoice_date).format('DD/MM/YYYY')}</TableCell>
                  <TableCell align="right">{invoice.amount}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button type="button" variant="secondary" size="sm" onClick={() => startEditing(invoice)} disabled={isSaving || isDeleting}>Editar</Button>
                      <Button type="button" variant="danger" size="sm" onClick={() => { setInvoiceToDelete(invoice); setDeleteError('') }} disabled={isSaving || isDeleting}>Eliminar</Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </TableContainer>}
        </Box>
      </DialogContent>
      {onClose && <DialogActions><Button type="button" variant="secondary" onClick={onClose} disabled={isSaving || isDeleting}>Cerrar</Button></DialogActions>}
    </Box>
  )

  const invoiceView = embedded ? (
    <Box sx={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }}>
      <InventoryDetailHeader><h6>Facturas del inventario</h6></InventoryDetailHeader>
      {content}
    </Box>
  ) : (
    <Dialog open onClose={isSaving || isDeleting ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="inventory-invoices-title">
      <DialogTitle id="inventory-invoices-title">Facturas de {inventory.name}</DialogTitle>
      {content}
    </Dialog>
  )

  return <>
    {invoiceView}
    <Dialog open={invoiceToDelete !== null} onClose={isDeleting ? undefined : () => setInvoiceToDelete(null)} fullWidth maxWidth="xs" aria-labelledby="delete-invoice-title">
      <DialogTitle id="delete-invoice-title">¿Eliminar la factura {invoiceToDelete?.invoice_number}?</DialogTitle>
      <DialogContent>
        <Typography>Esta factura se eliminará definitivamente del inventario.</Typography>
        {deleteError && <Typography color="error" sx={{ mt: 1 }}>{deleteError}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button type="button" variant="secondary" onClick={() => setInvoiceToDelete(null)} disabled={isDeleting}>Cancelar</Button>
        <Button type="button" variant="danger" onClick={confirmDelete} disabled={isDeleting}>Eliminar</Button>
      </DialogActions>
    </Dialog>
  </>
}

export const InventoryInvoicesPanel: FC<{ inventory: { id: number; name: string } }> = ({ inventory }) => (
  <InventoryInvoicesDialog inventory={inventory} embedded />
)

export default InventoryInvoicesDialog
